import { useEffect, useState } from "react";
import {
  Typography,
  Breadcrumb,
  Skeleton,
  Image,
  Tag,
  Space,
  Button,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useParams, Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import classes from "./ProjectNewsSinglePage.module.scss";
import axios from "../../../requests/axios";
import routes from "../../../requests/routes";

const { Title, Paragraph, Text } = Typography;

export default function ProjectNewsSinglePage() {
  const { projectIdOrSlug, newsIdOrSlug } = useParams();
  const navigate = useNavigate();

  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [miss, setMiss] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function fetchIt() {
      setLoading(true);
      setMiss(false);
      try {
        let res;
        if (projectIdOrSlug && newsIdOrSlug) {
          // preferred, scoped
          res = await axios.get(
            routes.capacityProjectNewsSingle(projectIdOrSlug, newsIdOrSlug)
          );
        } else {
          // fallback by news slug/id only
          const key = newsIdOrSlug || projectIdOrSlug;
          res = await axios.get(routes.capacityNewsSingle(key));
        }
        if (!mounted) return;
        setNews(res?.data?.news || null);
        setMiss(!res?.data?.news);
      } catch (_e) {
        if (!mounted) return;
        setNews(null);
        setMiss(true);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchIt();
    return () => {
      mounted = false;
    };
  }, [projectIdOrSlug, newsIdOrSlug]);

  const project = news?.project;

  return (
    <div className={classes.news}>
      <div className={classes.news__bread_crumbs}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Breadcrumb
          items={[
            {
              title: (
                <Link style={{ cursor: "pointer" }} to="/capacity-projects">
                  Projects
                </Link>
              ),
            },
            project
              ? {
                  title: (
                    <Link
                      style={{ cursor: "pointer" }}
                      to={`/capacity-projects/${project.slug || project._id}`}
                    >
                      {project.name || "Project"}
                    </Link>
                  ),
                }
              : { title: "Project" },
            { title: news?.title || "Update" },
          ]}
        />
      </div>

      {loading ? (
        <>
          <Skeleton active />
          <Skeleton.Image
            style={{ width: "100%", height: 280, margin: "16px 0" }}
          />
          <Skeleton active paragraph={{ rows: 6 }} />
        </>
      ) : miss ? (
        <div style={{ padding: "48px 0", textAlign: "center" }}>
          <Title level={3}>Update not found</Title>
          <Paragraph>It may have been removed or unpublished.</Paragraph>
        </div>
      ) : (
        <div className={classes.news__body}>
          <Title level={2} style={{ marginBottom: 4 }}>
            {news.title}
          </Title>
          <Space size="middle" wrap style={{ marginBottom: 12 }}>
            {news.publishDate && (
              <Text type="secondary">
                {dayjs(news.publishDate).format("MMM D, YYYY")}
              </Text>
            )}
            {Array.isArray(news.tags) &&
              news.tags.slice(0, 6).map((t) => (
                <Tag key={t} color="blue">
                  {t}
                </Tag>
              ))}
            {/* {project && (
              <Tag>
                <Link to={`/capacity-projects/${project.slug || project._id}`}>
                  {project.name}
                </Link>
              </Tag>
            )} */}
          </Space>

          {news.coverImage ? (
            <Image
              src={news.coverImage}
              alt={news.title}
              style={{
                width: "100%",
                maxHeight: 420,
                objectFit: "cover",
                margin: "8px 0 16px",
              }}
              preview
            />
          ) : null}

          {news.contentHtml ? (
            <div
              className="prose"
              style={{ fontSize: 16, lineHeight: 1.8 }}
              dangerouslySetInnerHTML={{ __html: news.contentHtml }}
            />
          ) : (
            <Paragraph>{news.contentText}</Paragraph>
          )}

          {/* Optional gallery */}
          {Array.isArray(news.gallery) && news.gallery.length > 0 && (
            <>
              <Title level={4} style={{ marginTop: 24 }}>
                Gallery
              </Title>
              <Space wrap size="middle">
                {news.gallery.map((url, i) => (
                  <Image
                    key={i}
                    src={url}
                    width={220}
                    height={140}
                    style={{ objectFit: "cover" }}
                  />
                ))}
              </Space>
            </>
          )}
        </div>
      )}
    </div>
  );
}
